import {
  Grid,
  Fade,
  Box,
  Popper,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  Stack,
  TextField,
  MenuItem,
} from "@mui/material";

import { FormFieldTextField } from "components/shared/components/FormFieldTextField";

import { CheckBoxLabel } from "components/Users/components/CheckBoxLabel";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { RegisterForm } from "lib/schema";
import { useEmailExistsQuery } from "components/Slices/usersApiSlice";

interface GeneralStepsProps {
  isLoading?: boolean;
}

export const GeneralSteps = ({ isLoading }: GeneralStepsProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { control, watch, getValues, formState } =
    useFormContext<RegisterForm>();

  const { data } = useEmailExistsQuery({ email: watch("email") });

  return (
    <Stack spacing={2} sx={{ width: "80%" }}>
      <Grid
        container
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Grid item xs={5}>
          <FormFieldTextField
            control={control}
            name="firstName"
            label="First Name"
            autoComplete="given-name"
            required
            fullWidth
            id="firstName"
            autoFocus
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={5}>
          <FormFieldTextField
            control={control}
            required
            fullWidth
            id="lastName"
            label="Last Name"
            name="lastName"
            autoComplete="family-name"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>
      <FormFieldTextField
        control={control}
        required
        fullWidth
        id="email"
        label="Email Address"
        name="email"
        autoComplete="email"
        InputLabelProps={{ shrink: true }}
        helperText={formState.errors.email?.message}
        rules={{
          required: "Email is required",
          pattern: {
            value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
            message: "Invalid email format",
          },
          validate: (value: string) => {
            if (data?.exists) {
              return "Email already exists";
            }
          },
        }}
      />
      <Popper
        sx={{ zIndex: 9999, fontSize: ".1rem" }}
        open={open}
        anchorEl={anchorEl}
        placement={"top-end"}
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={150}>
            <Paper sx={{ border: 1, padding: 2, borderRadius: "1rem" }}>
              <Typography sx={{ pb: 1, fontSize: ".75rem" }}>
                Passwords must contain atleast
              </Typography>
              <Box>
                <CheckBoxLabel
                  label="1 Uppercase letter"
                  checked={Boolean(watch("password")?.match(/[A-Z]/))}
                />
                <CheckBoxLabel
                  label="1 Special Character"
                  checked={Boolean(watch("password")?.match(/[#?!@$%^&*-]/))}
                />
                <CheckBoxLabel
                  label="1 Number"
                  checked={Boolean(watch("password")?.match(/[0-9]/))}
                />
                <CheckBoxLabel
                  label="10 Characters"
                  checked={watch("password")?.length >= 10}
                />
              </Box>
            </Paper>
          </Fade>
        )}
      </Popper>
      <FormFieldTextField
        control={control}
        required
        fullWidth
        name="password"
        label="Password"
        type={showPassword ? "text" : "password"}
        id="password"
        InputLabelProps={{ shrink: true }}
        rules={{
          required: "Password is required",
          minLength: {
            value: 10,
            message: "Password must be at least 10 characters",
          },
          pattern: {
            value:
              /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{10,}$/,
            message: "Invalid password format",
          },
        }}
        onClick={(event) => {
          setAnchorEl(event.currentTarget);
          setOpen(true);
        }}
        onBlur={() => {
          setOpen(false);
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <FormFieldTextField
        control={control}
        required
        fullWidth
        name="confirmPassword"
        label="Confirm Password"
        type={showConfirmPassword ? "text" : "password"}
        id="confirm"
        autoComplete="new-password"
        InputLabelProps={{ shrink: true }}
        rules={{
          required: "Passwords must match",
          validate: (val: string) => {
            if (val != getValues("password")) {
              return "Passwords must match";
            }
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Controller
        control={control}
        name="country"
        rules={{ required: "Country is required" }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            select
            required
            fullWidth
            label="Country"
            InputLabelProps={{ shrink: true }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message ?? "Sets your currency (USD or CAD)"}
          >
            <MenuItem value="US">United States (USD)</MenuItem>
            <MenuItem value="CA">Canada (CAD)</MenuItem>
          </TextField>
        )}
      />
    </Stack>
  );
};
